import { APP_NAME } from '@/branding';
import { config } from '@/config';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { CodeBlock } from '@/components/ui/code-block';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuthorizedAgents } from '@/components/account/AuthorizedAgents';

/**
 * Connect an AI assistant, and take one back.
 *
 * Personal settings rather than organization admin because a grant is bound to the
 * human who approved it: it inherits your permissions, it is capped by them as they
 * change, and it dies with your access. An administrator's view of everyone's
 * assistants is a separate surface with a separate access rule.
 *
 * Instructions and the list live on one page on purpose. Connecting and revoking are
 * the same decision at different times, and someone who has arrived here worried about
 * an assistant should not have to find a second page to end it.
 */

/**
 * The MCP endpoint, derived rather than configured.
 *
 * It is the API origin plus `/mcp` — the same derivation the marketing site makes
 * (`APP_URL + '/mcp'`). Deriving keeps development, staging and production each
 * printing their own endpoint instead of one hard-coded host that is wrong in two of
 * the three.
 */
const MCP_ENDPOINT = `${config.backendApi.replace(/\/+$/, '')}/mcp`;

/** The name each client lists the server under. */
const SERVER_NAME = APP_NAME.toLowerCase();

/**
 * Setup for the two clients most people bring, as their vendors document it
 * (2026-10): Claude Code adds a Streamable HTTP server with `claude mcp add
 * --transport http` and signs in from `/mcp`; Codex adds one with `codex mcp add
 * --url` and signs in with `codex mcp login`. Any other client gets the URL alone.
 */
const CLAUDE_CODE_COMMAND = `claude mcp add --transport http --scope user ${SERVER_NAME} ${MCP_ENDPOINT}`;
const CODEX_COMMANDS = `codex mcp add ${SERVER_NAME} --url ${MCP_ENDPOINT}\ncodex mcp login ${SERVER_NAME}`;

export default function AssistantsSettings() {
  return (
    <PageContainer>
      <PageHeader
        title="AI assistants"
        subtitle={`Connect an assistant to ${APP_NAME} over MCP, and revoke one at any time.`}
      />

      <div className="space-y-6">
        <section className="bg-card border border-border rounded-lg">
          <header className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Connect an assistant</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add this endpoint to any MCP client that signs in with OAuth. There is no API
              key to create, copy, or leak.
            </p>
          </header>

          <div className="p-5 space-y-4">
            <CodeBlock title="MCP server URL" code={MCP_ENDPOINT} />

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">1. Add it to your assistant</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {APP_NAME} signs in with OAuth, not a bearer token. If your client asks for a
                  token, an API key, or an OAuth client ID and secret, leave them empty: it
                  signs in through your browser instead.
                </p>
                <Tabs defaultValue="claude" className="mt-2">
                  <TabsList>
                    <TabsTrigger value="claude">Claude</TabsTrigger>
                    <TabsTrigger value="codex">Codex</TabsTrigger>
                    <TabsTrigger value="other">Other clients</TabsTrigger>
                  </TabsList>

                  <TabsContent value="claude" className="space-y-3">
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium">Claude Code</p>
                      <CodeBlock code={CLAUDE_CODE_COMMAND} />
                      <p className="text-xs text-muted-foreground">
                        Then run <code className="font-mono">/mcp</code> in Claude Code, choose{' '}
                        <span className="font-medium">{SERVER_NAME}</span>, and sign in.{' '}
                        <code className="font-mono">--scope user</code> adds it to every
                        project; leave it off to add it to the current project only.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium">Claude app (web, desktop and mobile)</p>
                      <p className="text-xs text-muted-foreground">
                        Open <span className="font-medium">Customize › Connectors</span> and
                        choose <span className="font-medium">Add custom connector</span>. Name it{' '}
                        {APP_NAME}, paste the URL above, select{' '}
                        <span className="font-medium">Add</span>, then{' '}
                        <span className="font-medium">Connect</span>. Leave the OAuth client ID
                        and secret empty. On a Team or Enterprise
                        plan, an Owner adds it first under{' '}
                        <span className="font-medium">Organization settings › Connectors</span>.
                        The Free plan allows one custom connector. The mobile apps use the
                        connectors you add here.
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="codex" className="space-y-3">
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium">Codex CLI</p>
                      <CodeBlock code={CODEX_COMMANDS} />
                      <p className="text-xs text-muted-foreground">
                        The login opens your browser. Run <code className="font-mono">/mcp</code>{' '}
                        in Codex to check it is connected.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium">Codex app</p>
                      <p className="text-xs text-muted-foreground">
                        Open <span className="font-medium">Settings › MCP servers</span> and add a
                        server. Name it {SERVER_NAME}, choose{' '}
                        <span className="font-medium">Streamable HTTP</span>, paste the URL above,
                        and leave the bearer token empty. Then select{' '}
                        <span className="font-medium">Authenticate</span>.
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      The Codex CLI, its IDE extension and the app share one setup: add it in
                      one and the others have it too.
                    </p>
                  </TabsContent>

                  <TabsContent value="other">
                    <p className="text-xs text-muted-foreground">
                      Paste the URL above wherever your assistant adds an MCP server, choosing
                      the Streamable HTTP transport if it asks. It registers itself and
                      discovers everything else from the URL.
                    </p>
                  </TabsContent>
                </Tabs>
              </div>

              <div>
                <p className="text-sm font-medium">2. Approve it, signed in as you</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  A client that has registered has no access at all until you approve it.
                  Your browser opens a consent page. You choose the{' '}
                  <span className="font-medium">context</span> the assistant may work in — one
                  organization or division, and nothing outside it — and whether it may only{' '}
                  <span className="font-medium">read and plan</span>, or also{' '}
                  <span className="font-medium">execute</span> the changes it drafts. Most
                  assistants only need read and plan.
                </p>
              </div>

              <div>
                <p className="text-sm font-medium">3. It stays inside your permissions</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  An assistant can never do more than you can. Its permissions are re-checked
                  against yours on every request, so if yours narrow, its do too. Drafted
                  changes still need a human to run them.
                </p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              A grant outlives your browser session — signing out does not end it. It does end
              when you revoke it below, when you change your password, or when you lose access
              to the organization it works in.
            </p>
          </div>
        </section>

        <AuthorizedAgents scope="mine" />
      </div>
    </PageContainer>
  );
}
